import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, IconButton, styled } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useGetBannersQuery } from "../../services/banners/banners";
import { BannerType } from "../../types/types";
import { resolveAssetUrl } from "../../utils/assets";
import { gaEvent } from "../../analytics";

// Bucket keys, not URLs (EB-514). The fallbacks are the same shape as an API banner, so they go
// through `resolveAssetUrl` at render like every other `image_url` — one call site, both sources.
const BANNERS_PREFIX = "banners";

const FALLBACK_BANNERS: BannerType[] = [
  {
    id: "fallback-1",
    title: "Alexa Amazon Echo Dot",
    image_url: `${BANNERS_PREFIX}/banner_alexa.jpg`,
    link_url: "",
    alt_text: "Alexa Amazon Echo Dot — $250,000 — Tu casa te escucha",
    display_order: 1,
    is_active: true,
  },
  {
    id: "fallback-2",
    title: "iPad A16",
    image_url: `${BANNERS_PREFIX}/banner_ipad_A16.jpg`,
    link_url: "",
    alt_text: 'iPad A16 11" — $2,070,000 — Tecnología que te acompaña',
    display_order: 2,
    is_active: true,
  },
  {
    id: "fallback-3",
    title: "Proyector HY300",
    image_url: `${BANNERS_PREFIX}/banner_proyector_HY300.jpg`,
    link_url: "",
    alt_text: "Proyector HY300 — $200,000 — Convierte cualquier pared en cine",
    display_order: 3,
    is_active: true,
  },
];

const AUTOPLAY_INTERVAL = 5000;

const CarouselContainer = styled(Box)({
  position: "relative",
  width: "100%",
  overflow: "hidden",
});

const BannerImage = styled("img")({
  width: "100%",
  display: "block",
  objectFit: "cover",
});

const BannerLink = styled("a")({
  display: "block",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.95,
  },
});

const ArrowButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "transparent",
  color: theme.palette.common.black,
  border: `2px solid ${theme.palette.common.black}`,
  padding: theme.spacing(0.25),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  zIndex: 1,
  [theme.breakpoints.up("sm")]: {
    border: `3px solid ${theme.palette.common.black}`,
    padding: theme.spacing(1),
  },
}));

const DotsContainer = styled(Box)({
  position: "absolute",
  bottom: 8,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: 6,
});

interface DotProps {
  active: boolean;
}

const Dot = styled("button", { shouldForwardProp: (prop) => prop !== "active" })<DotProps>(
  ({ theme, active }) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    padding: 0,
    backgroundColor: active ? theme.palette.common.white : theme.palette.action.disabled,
    transition: "background-color 0.3s",
  }),
);

const isExternalUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
};

const Banner: React.FC = () => {
  const { data: apiBanners } = useGetBannersQuery();
  const [current, setCurrent] = useState(0);

  const banners = useMemo(() => {
    const active = apiBanners?.filter((b) => b.is_active);
    return active && active.length > 0 ? active : FALLBACK_BANNERS;
  }, [apiBanners]);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const prev = () => {
    setCurrent((p) => (p - 1 + banners.length) % banners.length);
  };

  useEffect(() => {
    setCurrent(0);
  }, [banners]);

  useEffect(() => {
    const timer = setInterval(next, AUTOPLAY_INTERVAL);
    return () => clearInterval(timer);
  }, [next]);

  const currentBanner = banners[current];

  /**
   * `banner_click` (EB-558). The carousel is the top of the homepage and, until now, entirely
   * unmeasured: a banner links straight out to a product or an external page, so its traffic
   * arrives indistinguishable from any other referral and nobody can say whether the slot earns
   * the space it takes.
   *
   * `position` is the slide index, not the banner id, because the honest question is whether
   * anything past the first slide is ever seen — autoplay means most shoppers only ever look at
   * one. Both are sent so a poorly-performing *banner* and a poorly-performing *slot* stay
   * distinguishable.
   */
  const reportBannerClick = () =>
    gaEvent("banner_click", {
      banner_id: currentBanner.id,
      banner_title: currentBanner.title,
      position: current,
      link_url: currentBanner.link_url,
    });

  const renderBannerImage = () => (
    <BannerImage src={resolveAssetUrl(currentBanner.image_url)} alt={currentBanner.alt_text} />
  );

  return (
    <CarouselContainer data-testid="banner-carousel">
      {currentBanner.link_url ? (
        <BannerLink
          href={currentBanner.link_url}
          target={isExternalUrl(currentBanner.link_url) ? "_blank" : "_self"}
          rel={isExternalUrl(currentBanner.link_url) ? "noopener noreferrer" : undefined}
          data-testid="banner-link"
          onClick={reportBannerClick}
        >
          {renderBannerImage()}
        </BannerLink>
      ) : (
        renderBannerImage()
      )}

      {banners.length > 1 && (
        <>
          <ArrowButton onClick={prev} sx={{ left: 8 }} size="small" aria-label="Banner anterior">
            <ArrowBackIcon sx={{ strokeWidth: 1, stroke: "currentColor" }} />
          </ArrowButton>
          <ArrowButton onClick={next} sx={{ right: 8 }} size="small" aria-label="Siguiente banner">
            <ArrowForwardIcon sx={{ strokeWidth: 1, stroke: "currentColor" }} />
          </ArrowButton>

          <DotsContainer>
            {banners.map((_, idx) => (
              <Dot key={idx} active={idx === current} onClick={() => setCurrent(idx)} />
            ))}
          </DotsContainer>
        </>
      )}
    </CarouselContainer>
  );
};

export default Banner;
